import {
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useBucket3IncomeCategories } from "hooks/useBucket3IncomeCategories";
import { TRANSACTION_TYPE_CATEGORIES } from "helpers/transactionTypeCategories";

const LABEL = "Transaction types";
const LABEL_ID = "bucket3-income-types-label";

const LABEL_BY_VALUE = new Map(
  TRANSACTION_TYPE_CATEGORIES.map((category) => [
    category.value,
    category.label,
  ]),
);

/**
 * Which transaction types Bucket 3's Income Received adds up (task 6137):
 * Dividends by default, any of the Transactions page's categories besides.
 * Stored in the browser like the federal tax rate and sent with Calculate,
 * where the selection is part of the cached report's key -- so a changed
 * selection is calculated afresh rather than served from the cache.
 *
 * At least one type stays ticked: the last one cannot be unticked, since an
 * empty selection would leave the card nothing to count.
 */
const Bucket3IncomeTypesSetting = () => {
  const [categories, setCategories] = useBucket3IncomeCategories();

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setCategories(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id={LABEL_ID}>{LABEL}</InputLabel>
        <Select
          multiple
          labelId={LABEL_ID}
          value={categories}
          onChange={handleChange}
          input={<OutlinedInput label={LABEL} />}
          // Wrap rather than ellipsize: on a phone three picks no longer fit
          // one line, and a clipped "Margin I…" hides what is counted.
          sx={{ "& .MuiSelect-select": { whiteSpace: "normal" } }}
          renderValue={(picked) =>
            picked.map((value) => LABEL_BY_VALUE.get(value) ?? value).join(", ")
          }
        >
          {TRANSACTION_TYPE_CATEGORIES.map((category) => {
            const checked = categories.includes(category.value);
            return (
              <MenuItem
                key={category.value}
                value={category.value}
                disabled={checked && categories.length === 1}
              >
                <Checkbox checked={checked} size="small" />
                <ListItemText primary={category.label} />
              </MenuItem>
            );
          })}
        </Select>
        <FormHelperText>
          Counted in Bucket 3&apos;s Income Received
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

export default Bucket3IncomeTypesSetting;
